import React from 'react';
import styled, { css } from 'styled-components';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import {
    MetricsScrollable,
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import { blue10, blue100, slate10 } from '@zocdoc-mezzanine/foundations-colors';
import {
    headingSmall,
    headingExtraSmall,
    paragraphSmall,
    paragraphExtraSmall,
} from '@zocdoc-mezzanine/foundations-typography';

export const AppointmentInstructions = ({
    className,
    appointmentInstructions,
    isForReviewAndBookPage = false,
}) => {
    if (!appointmentInstructions || !appointmentInstructions.length) {
        return null;
    }

    if (isForReviewAndBookPage) {
        return (
            <MetricsScrollable
                element="MentalHealthPreAppointmentRequirements"
                initiator={EVENT_INITIATORS.USER}
                interactionType={INTERACTION_TYPES.VIEW}
                action={'Viewed Mental Health Pre-Appointment Requirements'}
            >
                {({ ref }) => (
                    <div className={className} ref={ref}>
                        <Wrapper
                            data-test="appointment-instructions-wrapper"
                            isForReviewAndBookPage={isForReviewAndBookPage}
                        >
                            {renderAppointmentInstructions(
                                appointmentInstructions,
                                isForReviewAndBookPage
                            )}
                        </Wrapper>
                    </div>
                )}
            </MetricsScrollable>
        );
    } else {
        return (
            <div className={className}>
                <Wrapper
                    data-test="appointment-instructions-wrapper"
                    isForReviewAndBookPage={isForReviewAndBookPage}
                >
                    {renderAppointmentInstructions(
                        appointmentInstructions,
                        isForReviewAndBookPage
                    )}
                </Wrapper>
            </div>
        );
    }
};

const renderAppointmentInstructions = (
    instructions,
    isForReviewAndBookPage
) => {
    return instructions.map((instruction, i) => {
        return (
            <InstructionWrapper
                key={i}
                isForReviewAndBookPage={isForReviewAndBookPage}
            >
                {renderInstruction(instruction, isForReviewAndBookPage)}
            </InstructionWrapper>
        );
    });
};

const renderInstruction = (instruction, isForReviewAndBookPage) => {
    if (!instruction.title || !instruction.body) {
        return null;
    }

    return (
        <>
            <Title isForReviewAndBookPage={isForReviewAndBookPage}>
                {instruction.title}
            </Title>
            {renderInstructionBodyLines(
                instruction.body,
                isForReviewAndBookPage
            )}
        </>
    );
};

const renderInstructionBodyLines = (body, isForReviewAndBookPage) => {
    return body.split(/\n/).map((line, i) => {
        return (
            <InstructionLine
                key={i}
                data-test={`appointment-instruction-line-${i}`}
                isForReviewAndBookPage={isForReviewAndBookPage}
            >
                {line}
            </InstructionLine>
        );
    });
};

const Wrapper = styled.div`
    ${(props) =>
        props.isForReviewAndBookPage ? wrapperCssForReviewAndBook : wrapperCss}
`;

const wrapperCss = css`
    color: ${blue100};
    text-align: left;
    background-color: ${slate10};
    padding: 8px 20px 4px 16px;

    ${breakpoint.medium} {
        background-color: inherit;
        width: 50%;
        padding-left: 0;
        padding-right: 0;
    }
`;

const wrapperCssForReviewAndBook = css`
    color: ${blue100};
    background-color: ${slate10};
    text-align: left;
`;

const InstructionWrapper = styled.div`
    ${(props) =>
        props.isForReviewAndBookPage
            ? instructionWrapperCssForReviewAndBook
            : instructionWrapperCss}
`;

const instructionWrapperCss = css`
    color: ${blue100};
`;

const instructionWrapperCssForReviewAndBook = css`
    background-color: ${slate10};
`;

const Title = styled.div`
    ${(props) =>
        props.isForReviewAndBookPage ? titleCssForReviewAndBook : titleCss}
`;

const titleCss = css`
    font-family: ${FontFamily.SharpSansSemibold};
    font-size: 16px;
    line-height: 24px;
    padding-top: 12px;
    padding-bottom: 3px;

    ${breakpoint.medium} {
        font-family: ${FontFamily.SharpSansSemibold};
        font-size: 20px;
        line-height: 25px;
    }
`;

const titleCssForReviewAndBook = css`
    background-color: ${blue10};
    ${headingExtraSmall};

    padding-left: 16px;
    padding-right: 16px;
    padding-top: 20px;

    ${breakpoint.medium} {
        ${headingSmall};
        padding-left: 20px;
        padding-right: 28px;
        padding-top: 24px;
    }
`;

const InstructionLine = styled.div`
    ${(props) =>
        props.isForReviewAndBookPage
            ? instructionLineCssForReviewAndBook
            : instructionLineCss}
`;

const instructionLineCss = css`
    font-family: ${FontFamily.SharpSansMedium};
    font-size: 14px;
    line-height: 21px;
    padding-bottom: 12px;

    ${breakpoint.medium} {
        line-height: 24px;
    }
`;

const instructionLineCssForReviewAndBook = css`
    background-color: ${blue10};
    ${paragraphExtraSmall};

    padding-left: 16px;
    padding-right: 16px;
    padding-bottom: 19px;
    padding-top: 12px;

    ${breakpoint.medium} {
        ${paragraphSmall};
        padding-left: 20px;
        padding-right: 28px;
        padding-bottom: 26px;
        padding-top: 8px;
    }
`;
